"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
/**
 * Returns a function that scrolls to a specific element on the page using a CSS selector.
 * Useful for navigating to form errors or key sections dynamically.
 * - If `offset` is provided, it will compute the correct scroll position and use `scrollTo()`.
 * - Otherwise, it falls back to `scrollIntoView()` with the given `scrollOptions`.
 *
 * @param {string} selector - CSS selector used to find the target element. Default is '.has-error'.
 * @param {string} containerSelector - Optional CSS selector for a scrollable container. If not provided, scrolls the window.
 * @param {ScrollIntoViewOptions} scrollOptions - Options for scrolling behavior. Default is `{ behavior: 'smooth', block: 'start' }`.
 * @param {number} offset - Optional vertical offset in pixels to adjust scroll position. Defaults to `0`. Useful with a sticky header.
 * @param {boolean} focus - To focus the element after scrolling. Defaults is `true`.
 * @param {boolean} focusSelector - CSS selector to find a focusable child element within the target. Default is `input, select, textarea, button, [tabindex]:not([tabindex="-1"])`.
 * @returns {Function} A function that scrolls to the selected element when called.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollTo
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus
 *
 * Example:
 *    const scrollToError = useScrollTo();
 *    scrollToError();
 */
const useScrollTo = ({ selector = '.has-error', containerSelector, scrollOptions = { behavior: 'smooth', block: 'start' }, offset = 0, focus = true, focusSelector = 'input, select, textarea, button, [tabindex]:not([tabindex="-1"])', } = {}) => {
    return (0, react_1.useCallback)(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined')
            return;
        const container = containerSelector
            ? document.querySelector(containerSelector)
            : null;
        const root = container ?? document;
        const element = root.querySelector(selector);
        if (!element)
            return;
        const behavior = scrollOptions.behavior ?? 'smooth';
        if (offset && offset !== 0) {
            if (container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const currentTop = container.scrollTop;
                const delta = elementRect.top - containerRect.top;
                const targetTop = Math.max(0, currentTop + delta - offset);
                container.scrollTo({ top: targetTop, behavior });
            }
            else {
                const scrollingElement = document.scrollingElement || document.documentElement;
                const y = (window.scrollY || scrollingElement.scrollTop || 0) +
                    element.getBoundingClientRect().top -
                    offset;
                window.scrollTo({ top: Math.max(0, y), behavior });
            }
        }
        else {
            element.scrollIntoView(scrollOptions);
        }
        if (focus) {
            requestAnimationFrame(() => {
                const focusable = element.querySelector(focusSelector) ?? element;
                if (focusable.tabIndex < 0)
                    focusable.tabIndex = -1;
                focusable.focus({ preventScroll: true });
            });
        }
    }, [selector, containerSelector, scrollOptions, offset, focus, focusSelector]);
};
exports.default = useScrollTo;
//# sourceMappingURL=useScrollTo.js.map